/**
 * Portfolio – personal net worth tracker (assets + income).
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/portfolio/
 * Backend: portfolio.routes.ts (personal assets/income). Wired to
 * GET/POST/DELETE /api/v1/portfolio/assets, /api/v1/portfolio/income,
 * and GET /api/v1/portfolio/net-worth.
 */
import React, { useEffect, useState } from 'react';
import { portfolioApi, type PersonalAssetItem, type PersonalIncomeItem } from '../services/api';

type AssetType = 'property' | 'vehicle' | 'investment' | 'business' | 'savings' | 'other';
type IncomeType = 'salary' | 'freelance' | 'investment' | 'other';

interface NetWorthSummary {
  netWorth: number;
  totalAssetValue: number;
  totalLiabilities: number;
  byType: Record<string, { value: number; liability: number; net: number }>;
}

const ASSET_TYPES: AssetType[] = ['property', 'vehicle', 'investment', 'business', 'savings', 'other'];
const INCOME_TYPES: IncomeType[] = ['salary', 'freelance', 'investment', 'other'];

const currency = (n: number) => `$${n.toLocaleString()}`;

const Portfolio: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<NetWorthSummary | null>(null);
  const [assets, setAssets] = useState<PersonalAssetItem[]>([]);
  const [income, setIncome] = useState<PersonalIncomeItem[]>([]);

  const [showAddAsset, setShowAddAsset] = useState(false);
  const [newAssetName, setNewAssetName] = useState('');
  const [newAssetType, setNewAssetType] = useState<AssetType>('property');
  const [newAssetValue, setNewAssetValue] = useState('');
  const [newAssetCostBasis, setNewAssetCostBasis] = useState('');
  const [newAssetLiability, setNewAssetLiability] = useState('');
  const [newAssetNotes, setNewAssetNotes] = useState('');

  const [showAddIncome, setShowAddIncome] = useState(false);
  const [newIncomeSource, setNewIncomeSource] = useState('');
  const [newIncomeType, setNewIncomeType] = useState<IncomeType>('salary');
  const [newIncomeGross, setNewIncomeGross] = useState('');
  const [newIncomeNet, setNewIncomeNet] = useState('');

  const [submittingAsset, setSubmittingAsset] = useState(false);
  const [submittingIncome, setSubmittingIncome] = useState(false);
  const [deletingAsset, setDeletingAsset] = useState<string | null>(null);
  const [deletingIncome, setDeletingIncome] = useState<string | null>(null);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [summaryRes, assetsRes, incomeRes] = await Promise.all([
        portfolioApi.netWorth(),
        portfolioApi.listAssets(),
        portfolioApi.listIncome(),
      ]);

      if (summaryRes && (summaryRes.success === undefined || summaryRes.success)) {
        setSummary((summaryRes.data ?? summaryRes) as NetWorthSummary);
      }
      if (assetsRes?.success && Array.isArray(assetsRes?.data)) {
        setAssets(assetsRes.data);
      } else if (Array.isArray(assetsRes)) {
        setAssets(assetsRes);
      }
      if (incomeRes?.success && Array.isArray(incomeRes?.data)) {
        setIncome(incomeRes.data);
      } else if (Array.isArray(incomeRes)) {
        setIncome(incomeRes);
      }

      if (assetsRes?.error) setError(assetsRes.error);
    } catch {
      setError('Failed to load portfolio');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddAsset = async () => {
    if (!newAssetName.trim() || !newAssetValue) return;
    setSubmittingAsset(true);
    setError(null);
    try {
      const res = await portfolioApi.createAsset({
        name: newAssetName.trim(),
        assetType: newAssetType,
        currentValue: Number(newAssetValue),
        costBasis: newAssetCostBasis ? Number(newAssetCostBasis) : undefined,
        liability: newAssetLiability ? Number(newAssetLiability) : undefined,
        notes: newAssetNotes.trim() || undefined,
      });
      if (res?.success === false) {
        setError(res?.error || 'Failed to add asset');
      } else {
        setNewAssetName('');
        setNewAssetValue('');
        setNewAssetCostBasis('');
        setNewAssetLiability('');
        setNewAssetNotes('');
        setShowAddAsset(false);
        loadData(true);
      }
    } catch {
      setError('Failed to add asset');
    } finally {
      setSubmittingAsset(false);
    }
  };

  const handleDeleteAsset = async (id: string) => {
    setDeletingAsset(id);
    setError(null);
    try {
      const res = await portfolioApi.deleteAsset(id);
      if (res?.success === false) setError(res?.error || 'Failed to delete asset');
      else loadData(true);
    } catch {
      setError('Failed to delete asset');
    } finally {
      setDeletingAsset(null);
    }
  };

  const handleAddIncome = async () => {
    if (!newIncomeSource.trim() || !newIncomeGross) return;
    setSubmittingIncome(true);
    setError(null);
    try {
      const res = await portfolioApi.createIncome({
        source: newIncomeSource.trim(),
        incomeType: newIncomeType,
        grossAmount: Number(newIncomeGross),
        netAmount: newIncomeNet ? Number(newIncomeNet) : undefined,
      });
      if (res?.success === false) {
        setError(res?.error || 'Failed to add income');
      } else {
        setNewIncomeSource('');
        setNewIncomeGross('');
        setNewIncomeNet('');
        setShowAddIncome(false);
        loadData(true);
      }
    } catch {
      setError('Failed to add income');
    } finally {
      setSubmittingIncome(false);
    }
  };

  const handleDeleteIncome = async (id: string) => {
    setDeletingIncome(id);
    setError(null);
    try {
      const res = await portfolioApi.deleteIncome(id);
      if (res?.success === false) setError(res?.error || 'Failed to delete income');
      else loadData(true);
    } catch {
      setError('Failed to delete income');
    } finally {
      setDeletingIncome(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  const byType = summary?.byType || {};
  const typeEntries = ASSET_TYPES.filter((t) => byType[t]).map((t) => ({ type: t, ...byType[t] }));
  const maxValue = Math.max(1, ...typeEntries.map((t) => Math.max(0, t.value)));

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '26px' }}>📊 Portfolio</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '15px' }}>
              Track your net worth: assets, investments, and income
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadData(true)}
            disabled={refreshing}
            style={{
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '8px',
              color: 'white',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}

      {/* Net worth summary */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>Net Worth</h2>
        {summary ? (
          <>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#11998e', marginBottom: '12px' }}>
              {currency(summary.netWorth)}
            </div>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Total assets</div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#333' }}>{currency(summary.totalAssetValue)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Total liabilities</div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#b91c1c' }}>{currency(summary.totalLiabilities)}</div>
              </div>
            </div>

            {typeEntries.length > 0 ? (
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>By asset type</h3>
                {typeEntries.map(({ type, value, liability, net }) => (
                  <div key={type} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ textTransform: 'capitalize', color: '#333', fontWeight: 600 }}>{type}</span>
                      <span style={{ color: '#666' }}>
                        {currency(value)}{liability ? ` − ${currency(liability)} = ${currency(net)}` : ''}
                      </span>
                    </div>
                    <div style={{ background: '#eee', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${(Math.max(0, value) / maxValue) * 100}%`,
                          background: '#11998e',
                          height: '100%',
                          borderRadius: '6px',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>Add assets below to see a breakdown by type.</p>
            )}
          </>
        ) : (
          <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>No net worth data yet.</p>
        )}
      </div>

      {/* Assets section */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Assets</h2>
          <button
            type="button"
            onClick={() => setShowAddAsset((s) => !s)}
            style={{
              padding: '8px 16px',
              background: '#11998e',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {showAddAsset ? 'Close' : 'Add Asset'}
          </button>
        </div>

        {showAddAsset && (
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
            <input
              type="text"
              placeholder="Asset name (e.g. Home, Car, Brokerage account)"
              value={newAssetName}
              onChange={(e) => setNewAssetName(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <select
              value={newAssetType}
              onChange={(e) => setNewAssetType(e.target.value as AssetType)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            >
              {ASSET_TYPES.map((t) => (
                <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Current value"
              value={newAssetValue}
              onChange={(e) => setNewAssetValue(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <input
              type="number"
              placeholder="Cost basis (optional)"
              value={newAssetCostBasis}
              onChange={(e) => setNewAssetCostBasis(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <input
              type="number"
              placeholder="Liability / amount owed (optional)"
              value={newAssetLiability}
              onChange={(e) => setNewAssetLiability(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <input
              type="text"
              placeholder="Notes (optional)"
              value={newAssetNotes}
              onChange={(e) => setNewAssetNotes(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleAddAsset}
                disabled={submittingAsset || !newAssetName.trim() || !newAssetValue}
                style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submittingAsset ? 'not-allowed' : 'pointer', fontSize: '14px' }}
              >
                {submittingAsset ? 'Adding…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddAsset(false)}
                style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {assets.length === 0 ? (
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
            No assets yet. Add a property, vehicle, investment, or other asset above.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {assets.map((a) => (
              <li
                key={a.id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div>
                  <span style={{ fontWeight: 600, color: '#333' }}>{a.name}</span>
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '11px',
                    color: '#11998e',
                    background: '#e6fbf5',
                    borderRadius: '999px',
                    padding: '2px 8px',
                    textTransform: 'capitalize',
                  }}>
                    {a.assetType}
                  </span>
                  {a.notes && <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{a.notes}</p>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: '#11998e' }}>{currency(a.currentValue)}</div>
                    {!!a.liability && (
                      <div style={{ fontSize: '12px', color: '#b91c1c' }}>−{currency(a.liability)} owed</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteAsset(a.id)}
                    disabled={deletingAsset === a.id}
                    style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: deletingAsset === a.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                  >
                    {deletingAsset === a.id ? '…' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Income section */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Income</h2>
          <button
            type="button"
            onClick={() => setShowAddIncome((s) => !s)}
            style={{
              padding: '8px 16px',
              background: '#11998e',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {showAddIncome ? 'Close' : 'Add Income'}
          </button>
        </div>

        {showAddIncome && (
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#64748b' }}>
              This is where payslips and other income get logged.
            </p>
            <input
              type="text"
              placeholder="Source (e.g. Employer, Client name)"
              value={newIncomeSource}
              onChange={(e) => setNewIncomeSource(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <select
              value={newIncomeType}
              onChange={(e) => setNewIncomeType(e.target.value as IncomeType)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            >
              {INCOME_TYPES.map((t) => (
                <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Gross amount"
              value={newIncomeGross}
              onChange={(e) => setNewIncomeGross(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <input
              type="number"
              placeholder="Net amount (optional, e.g. after tax)"
              value={newIncomeNet}
              onChange={(e) => setNewIncomeNet(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleAddIncome}
                disabled={submittingIncome || !newIncomeSource.trim() || !newIncomeGross}
                style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submittingIncome ? 'not-allowed' : 'pointer', fontSize: '14px' }}
              >
                {submittingIncome ? 'Adding…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddIncome(false)}
                style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {income.length === 0 ? (
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
            No income logged yet. Add a payslip or other income above.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {income.map((i) => (
              <li
                key={i.id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div>
                  <span style={{ fontWeight: 600, color: '#333' }}>{i.source}</span>
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '11px',
                    color: '#11998e',
                    background: '#e6fbf5',
                    borderRadius: '999px',
                    padding: '2px 8px',
                    textTransform: 'capitalize',
                  }}>
                    {i.incomeType}
                  </span>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                    {new Date(i.receivedAt).toLocaleDateString()}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: '#11998e' }}>{currency(i.grossAmount)}</div>
                    {i.netAmount != null && (
                      <div style={{ fontSize: '12px', color: '#64748b' }}>net {currency(i.netAmount)}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteIncome(i.id)}
                    disabled={deletingIncome === i.id}
                    style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: deletingIncome === i.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                  >
                    {deletingIncome === i.id ? '…' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Portfolio;
